import services from "@/configs/axios";
import CONFIGS from "@/constants/env";
import {
  IAuthData,
  IAuthGetCode,
  ILoginWithProvider,
  IRegisterData,
  IResetPassword,
  IResponseSignInDiscord,
} from "@/features/authentication/interfaces/IAuth";
import {
  IResponseFormatWithToken,
  IResponseRefreshToken,
} from "@/interfaces/IHelper";
import { onGetCookie } from "@/utils/handleCookie";

export const resetPassword = async ({
  email,
  password,
  confirmPassword,
  token,
}: Pick<
  IResetPassword,
  "email" | "password" | "confirmPassword" | "token"
>) => {
  const data = { email, password, confirmPassword, token };
  try {
    const response = await fetch(
      `${CONFIGS.API}/user/forgot-password/reset-password`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      },
    );

    const result = await response.json();

    if (!response.ok) {
      console.error("Failed to register:", result.message);
      return { status: false, message: result.message };
    }

    return result;
  } catch (error) {
    console.error("Error logging in:", error);
  }
};

export const forgotPassword = async ({
  email,
}: Pick<IRegisterData, "email">) => {
  try {
    const response = await fetch(
      `${CONFIGS.API}/user/forgot-password/${email}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    const result = await response.json();

    if (!response.ok) {
      console.error("Failed to forgotPassword:", result.message);
      return { status: false, message: result.message };
    }

    return result;
  } catch (error) {
    console.error("Error forgotPassword", error);
  }
};

export const register = async ({
  email,
  password,
  otp_number,
}: Pick<IRegisterData, "email" | "password" | "otp_number">) => {
  const data = { email, password, otp_number };
  try {
    const response = await fetch(`${CONFIGS.API}/signup`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });

    const result = await response.json();

    if (!response.ok) {
      return Promise.reject(result);
    }

    return result as IResponseFormatWithToken;
  } catch (error) {
    return Promise.reject(error);
  }
};

export const login = ({
  email,
  password,
}: Pick<IAuthData, "email" | "password">) =>
  new Promise<IResponseFormatWithToken>((resolve, reject) => {
    const data = { email, password };
    services
      .post<IResponseFormatWithToken>("/login", data)
      .then((response) => {
        resolve(response.data);
      })
      .catch((error) => {
        reject(error);
      });
  });

export const refreshToken = ({ refreshToken }: { refreshToken: string }) =>
  new Promise<IResponseRefreshToken>((resolve, reject) => {
    const data = { refreshToken };
    services
      .post<IResponseRefreshToken>("/refresh-token", data)
      .then((response) => {
        resolve(response.data);
      })
      .catch((error) => {
        reject(error);
      });
  });

/**
 * @description get code from api and return data from api
 */
export const getCodeOTP = async ({ email }: Pick<IAuthGetCode, "email">) => {
  try {
    const response = await fetch(`${CONFIGS.API}/send-otp-email/${email}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        // Recaptcha token
        // "g-recaptcha-token": recaptcha,
      },
    });

    const data = await response.json();

    if (!response.ok) {
      console.error("Failed to get code");
      return Promise.reject(data);
    }

    return data as {
      status: boolean;
      message?: string;
    };
  } catch (error) {
    console.error("Error getting code:", error);
    return Promise.reject(error);
  }
};

// TODO: Chage to this when API is ready
/* export const getSignUpVerifyCode = ({
  email,
  recaptcha,
}: {
  email: string;
  recaptcha: string;
}) =>
  new Promise<{ message: string }>((resolve, reject) => {
    services
      .get<{ message: string }>(`/profile/getcode/${email}`, {
        headers: {
          'g-recaptcha-token': recaptcha,
        },
      })
      .then((response) => {
        resolve(response.data);
      })
      .catch((error) => reject(error));
  });
*/

// logout
export const logout = async () => {
  const cookies = onGetCookie();
  const response = await services.post(`${CONFIGS.API}/logout`, {
    token: cookies?.refresh_token,
  });
  return response.data;
};

// login with provider
export const loginWithProvider = async ({
  email,
  provider,
  providerUUID,
}: ILoginWithProvider) => {
  const data = { email, provider, providerUUID };
  try {
    const response = await fetch(`${CONFIGS.API}/sign-in/with-provider`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });

    const result = await response.json();

    if (!response.ok) {
      console.error("Failed to login:", result.message);
      return { status: false, message: result.message };
    }

    return result;
  } catch (error) {
    console.error("Error logging in:", error);
  }
};

export const loginWithDiscord = ({
  discord_id,
  email,
}: {
  discord_id: string;
  email: string;
}) =>
  new Promise<IResponseSignInDiscord>((resolve, reject) => {
    const data = { discord_id, email };
    services
      .post<IResponseSignInDiscord>("/sign-in/with-discord", data)
      .then((response) => {
        resolve(response.data);
      })
      .catch((error) => {
        reject(error);
      });
  });
